import { CircularProgress, makeStyles } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import {
  attachFileToCustomQuestion,
  getRespondersCustomQuestion,
  getRespondersCustomQuestionByTenantId,
} from '../../../api/CustomQuestion';
import { Audience } from '../../../api/CustomQuestion/types/Audience';
import { RespondersCustomQuestions } from '../../../api/CustomQuestion/types/CustomQuestion';
import { CustomQuestionType } from '../../../api/CustomQuestion/types/Type';
import { UserContext } from '../../../contexts/user-context';
import { useScrollOnValidation } from '../../../hooks/useScrollOnValidation';
import {
  getContentTypeFromExtension,
  getCustomQuestionsDefaultValues,
  getCustomQuestionsValidationSchema,
  handleSubmitCustomForm,
  customQuestionFileExtensions,
} from '../../../utils/custom-questions';
import { FILE_SIZE_LIMIT, FILE_SIZE_LIMIT_MB } from '../../../utils/form';
import { CLASS_TRACKING } from '../../../utils/tracking_class';
import { yupValidate } from '../../../utils/yup';
import {
  AlertState,
  BeforeUnload,
  Button,
  PageLoader,
  SnackMessage,
  StickyContent,
} from '../../common';
import {
  Attachment,
  getAttachmentFromFile,
} from '../../common/attachment-card';
import CustomFormField from './custom-form-field';

interface CustomFormsProps {
  audience: Audience;
  responderId: string;
  successMessage?: string;
  hasAccessToUpdate?: boolean;
  isExternalPage?: boolean;
}

enum CustomFormStates {
  LOADING = 'LOADING',
  READY = 'READY',
  ERROR = 'ERROR',
  EMPTY = 'EMPTY',
}

const useCustomFormsStyles = makeStyles((theme) => ({
  loader: {
    marginTop: 200,
  },
  sectionBlock: {
    '& + &': {
      marginTop: 56,
    },
  },
  mainFormBlock: {
    width: '100%',

    [theme.breakpoints.up('sm')]: {
      width: 560,
    },
  },
  actionsBlock: {
    marginTop: 56,
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 696,
  },
}));

function CustomForm({
  audience,
  responderId,
  successMessage,
  hasAccessToUpdate,
  isExternalPage,
}: CustomFormsProps) {
  const classes = useCustomFormsStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useContext(UserContext);
  const setSubmitValidationFailed = useScrollOnValidation();

  const [state, setState] = useState<CustomFormStates>(
    CustomFormStates.LOADING,
  );
  const [respondersCustomQuestions, setRespondersCustomQuestions] =
    useState<RespondersCustomQuestions | null>(null);
  const [defaultValues, setDefaultValues] = useState<Record<string, any>>({});
  const [submitting, setSubmitting] = useState(false);
  const [attachments, setAttachments] = useState<Map<string, Attachment>>(
    new Map(),
  );

  const validationSchema = useMemo(
    () =>
      getCustomQuestionsValidationSchema(respondersCustomQuestions?.questions),
    [respondersCustomQuestions],
  );

  const validate = useMemo(() => {
    const baseValidate =
      validationSchema && hasAccessToUpdate
        ? yupValidate(validationSchema)
        : undefined;

    return (values: Record<string, any>) => {
      const errors: Record<string, any> = baseValidate
        ? baseValidate(values)
        : {};

      if (respondersCustomQuestions?.questions?.length) {
        respondersCustomQuestions.questions.forEach((q) => {
          if (q.type === CustomQuestionType.ATTACHMENT) {
            const attachment = attachments.get(q.id);

            // Required validation
            if (q.isMandatory && (!attachment || !attachment.url)) {
              errors[q.id] = 'Required field';
              return;
            }

            if (attachment) {
              // Type validation
              const extWithDot = `.${(
                attachment.extension || ''
              ).toLowerCase()}`;
              if (
                extWithDot &&
                !customQuestionFileExtensions.includes(extWithDot)
              ) {
                errors[q.id] =
                  'Unsupported file type. Allowed: pdf, ppt, pptx, doc, docx, xls, xlsx, txt, png, jpeg, jpg, gif';
                return;
              }

              // Size validation (<= FILE_SIZE_LIMIT)
              if (attachment.size > FILE_SIZE_LIMIT) {
                errors[
                  q.id
                ] = `File size must be ${FILE_SIZE_LIMIT_MB}MB or less`;
                return;
              }
            }
          }
        });
      }

      return errors;
    };
  }, [
    validationSchema,
    hasAccessToUpdate,
    respondersCustomQuestions,
    attachments,
  ]);

  const handleSubmit = async (values: Record<string, any>) => {
    try {
      if (respondersCustomQuestions && responderId && user?.id) {
        setSubmitting(true);
        const { questions } = respondersCustomQuestions;

        const updatedValues = { ...values };
        questions.forEach((q) => {
          if (q.type === CustomQuestionType.ATTACHMENT) {
            const attachment = attachments.get(q.id);
            if (attachment?.url) {
              // Create AttachmentMetadata object for backend
              // Format date for LocalDateTime: YYYY-MM-DDTHH:mm:ss (no milliseconds, no timezone)
              const now = new Date();
              const uploadDate = now.toISOString().split('.')[0]; // Remove milliseconds

              const attachmentMetadata = {
                filePath: attachment.url,
                fileName: `${attachment.name}.${attachment.extension}`,
                fileSize: attachment.size,
                contentType: getContentTypeFromExtension(attachment.extension),
                uploadDate: uploadDate,
              };
              // Serialize to JSON string for backend
              updatedValues[q.id] = JSON.stringify(attachmentMetadata);
            } else {
              // Clear attachment value when file is removed
              updatedValues[q.id] = '';
            }
          }
        });

        await handleSubmitCustomForm({
          values: updatedValues,
          questions,
          responderId,
          tenantId: user.id,
          audience,
        });
        setDefaultValues(updatedValues);
        setSubmitting(false);
        enqueueSnackbar(successMessage || 'Successfully updated.', {
          variant: 'success',
        });
      }
    } catch (e: any) {
      const messageError = e.response?.data?.message;

      enqueueSnackbar('An error occurred while saving. Please, try again.', {
        content: (key, message) =>
          SnackMessage({
            key,
            message,
            variant: 'error',
            additionalMessage: messageError,
          }),
        variant: 'error',
      });
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const getCustomQuestions = async () => {
      if (user && responderId) {
        try {
          setState(CustomFormStates.LOADING);
          const newRespondersCustomQuestions = isExternalPage
            ? await getRespondersCustomQuestionByTenantId(
                user.id,
                responderId,
                audience,
              )
            : await getRespondersCustomQuestion(responderId, audience);
          if (!newRespondersCustomQuestions.questions?.length) {
            setState(CustomFormStates.EMPTY);
            return;
          }
          const sortedRespondersCustomQuestions = {
            ...newRespondersCustomQuestions,
            questions: newRespondersCustomQuestions.questions.sort(
              (a, b) => a.position - b.position,
            ),
          };
          setRespondersCustomQuestions(sortedRespondersCustomQuestions);
          const defaultValues = getCustomQuestionsDefaultValues(
            sortedRespondersCustomQuestions.questions,
          );
          setDefaultValues(defaultValues);

          // Initialize attachments for ATTACHMENT questions
          const initialAttachments = new Map<string, Attachment>();
          sortedRespondersCustomQuestions.questions.forEach((question) => {
            if (
              question.type === CustomQuestionType.ATTACHMENT &&
              question.response?.value
            ) {
              try {
                // Parse AttachmentMetadata from value
                const metadata = JSON.parse(question.response.value);
                // metadata is an object with filePath, fileName, fileSize, contentType
                if (metadata.filePath) {
                  const fileName = metadata.fileName || 'file';
                  const nameParts = fileName.split('.');
                  const extension = nameParts.pop() || '';
                  const name = nameParts.join('.');

                  initialAttachments.set(question.id, {
                    name: name,
                    extension: extension,
                    size: metadata.fileSize || 0,
                    url: metadata.filePath,
                  });
                }
              } catch (e) {
                // Fallback for old format where value was just a URL string
                const url = question.response.value;
                const fileName = url.split('/').pop() || 'file';
                const extension = fileName.split('.').pop() || '';
                initialAttachments.set(question.id, {
                  name: fileName,
                  extension: extension,
                  size: 0,
                  url: url,
                });
              }
            }
          });
          setAttachments(initialAttachments);

          setState(CustomFormStates.READY);
        } catch (error: any) {
          setState(CustomFormStates.ERROR);
          const messageError = error?.response?.data?.message;
          enqueueSnackbar(
            'An error occurred while loading the form. Please, try again.',
            {
              content: (key, message) =>
                SnackMessage({
                  key,
                  message,
                  variant: 'error',
                  additionalMessage: messageError,
                }),
              variant: 'error',
            },
          );
        }
      }
    };
    getCustomQuestions();
  }, [audience, user, responderId, enqueueSnackbar, isExternalPage]);

  if (state === CustomFormStates.LOADING) {
    return (
      <div className={classes.loader}>
        <PageLoader />
      </div>
    );
  }

  if (state === CustomFormStates.ERROR) {
    return (
      <div className={classes.error}>
        <AlertState type='error'>
          An error occurred while loading the form. Please, try again.
        </AlertState>
      </div>
    );
  }

  if (state === CustomFormStates.EMPTY) {
    return (
      <div className={classes.error}>
        <AlertState type='warning'>
          The form is currently unavailable as it is not configured. Please
          contact the administration for assistance.
        </AlertState>
      </div>
    );
  }

  const handleUploadFileForQuestion = async (
    questionId: string,
    file: File,
  ): Promise<string> => {
    if (!user?.id) throw new Error('User ID is required');

    const response = await attachFileToCustomQuestion(
      user.id,
      questionId,
      responderId,
      file,
    );

    const filePath =
      typeof response === 'string'
        ? response
        : (response as any)?.filePath || '';

    const attachment = getAttachmentFromFile(file);
    attachment.url = filePath;

    setAttachments((prev) => {
      const newMap = new Map(prev);
      newMap.set(questionId, attachment);
      return newMap;
    });

    return filePath;
  };

  return (
    <div>
      <Form
        validate={validate}
        onSubmit={handleSubmit}
        initialValues={defaultValues}
        keepDirtyOnReinitialize
        render={(formProps) => {
          setSubmitValidationFailed(
            formProps.submitFailed &&
              !formProps.dirtySinceLastSubmit &&
              !formProps.submitting,
          );
          return (
            <div>
              <BeforeUnload
                when={formProps.dirty && !submitting}
                title='Leave the page'
                body='You are about to leave the page, all unsaved changes will be lost. Do you want to continue?'
                disabled={submitting}
                confirmButtonRenderer={({ onConfirm }) => (
                  <Button
                    variant='outlined'
                    onClick={async () => {
                      await formProps.handleSubmit();
                      onConfirm();
                    }}
                    disabled={submitting || !formProps.valid}>
                    {submitting ? (
                      <CircularProgress size={24} color='inherit' />
                    ) : (
                      'Save the changes'
                    )}
                  </Button>
                )}
              />
              <div className={classes.mainFormBlock}>
                <form noValidate>
                  <div className={classes.sectionBlock}>
                    {respondersCustomQuestions?.questions.map((question) => (
                      <CustomFormField
                        key={question.id}
                        question={question}
                        hasAccessToUpdate={hasAccessToUpdate}
                        onChange={(value) =>
                          formProps.form.change(question.id, value)
                        }
                        value={formProps.values[question.id] ?? ''}
                        onUploadFile={
                          question.type === CustomQuestionType.ATTACHMENT
                            ? (file: File) =>
                                handleUploadFileForQuestion(question.id, file)
                            : undefined
                        }
                        attachment={
                          question.type === CustomQuestionType.ATTACHMENT
                            ? attachments.get(question.id)
                            : undefined
                        }
                        onFileRemove={
                          question.type === CustomQuestionType.ATTACHMENT
                            ? () => {
                                setAttachments((prev) => {
                                  const newMap = new Map(prev);
                                  newMap.delete(question.id);
                                  return newMap;
                                });
                              }
                            : undefined
                        }
                        error={formProps.errors?.[question.id]}
                        submitFailed={formProps.submitFailed}
                      />
                    ))}
                  </div>

                  {hasAccessToUpdate && (
                    <div className={classes.actionsBlock}>
                      <StickyContent>
                        <Button
                          className={CLASS_TRACKING.INTERNAL_ACTION}
                          onClick={formProps.handleSubmit}
                          disabled={submitting}
                          startIcon={<CheckIcon />}>
                          {submitting ? (
                            <CircularProgress size={24} color='inherit' />
                          ) : (
                            'Save'
                          )}
                        </Button>
                      </StickyContent>
                    </div>
                  )}
                </form>
              </div>
            </div>
          );
        }}
      />
    </div>
  );
}

export default CustomForm;
